import React from 'react';
import { contains, pluck, difference, append } from 'ramda';
import { shape, number, arrayOf, string, bool, func } from 'prop-types';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';

import EmptyTable from './EmptyTable';
import LocationTableRow from './LocationTableRow';
import CompareTableRow from './CompareTableRow';

import { Sort, Compare } from '../../constants';
import { Sort as ISort, Precision as IPrecision } from '../../models';

const ROWS_PER_PAGE = 25;

const isCompare = compare =>
  [Compare.YEAR, Compare.PERIOD, Compare.CUSTOM].indexOf(compare) >= 0;

const getColumns = (compare, censusView, crimeType) => {
  if (isCompare(compare)) {
    return [
      { prop: Sort.NAME, display: censusView ? 'Census tract' : 'City' },
      { prop: Sort.TOTAL, display: `${crimeType} Change` },
      { prop: Sort.RATE, display: 'Rate/100k Change' },
      { prop: Sort.TI_1_MURDERS, display: `TI 1 ${crimeType}` },
      { prop: Sort.TI_2_MURDERS, display: `TI 2 ${crimeType}` },
      {
        prop: Sort.TI_1_RATE,
        display: censusView ? 'TI 1 Rate/1k' : 'TI 1 Rate/100k',
      },
      {
        prop: Sort.TI_2_RATE,
        display: censusView ? 'TI 2 Rate/1k' : 'TI 2 Rate/100k',
      },
      { prop: Sort.TI_1_POPULATION, display: 'TI 1 Population' },
      { prop: Sort.TI_2_POPULATION, display: 'TI 2 Population' },
    ];
  }

  if (compare === 'recurring') {
    return [
      { prop: Sort.NAME, display: censusView ? 'Census tract' : 'City' },
      { prop: Sort.TIMESPAN, display: 'Timespan' },
      { prop: Sort.TOTAL, display: `Avg. # of ${crimeType}` },
      {
        prop: Sort.RATE,
        display: censusView ? 'Avg. Rate/1k' : 'Avg. Rate/100k',
      },
      { prop: Sort.POPULATION, display: 'Avg. Population' },
    ];
  }

  if (censusView) {
    return [
      { prop: Sort.NAME, display: 'Census tract' },
      { prop: Sort.TIMESPAN, display: 'Timespan' },
      { prop: Sort.TOTAL, display: crimeType },
      { prop: Sort.POPULATION, display: 'Avg. Population' },
    ];
  }

  return [
    { prop: Sort.NAME, display: 'City' },
    { prop: Sort.TIMESPAN, display: 'Timespan' },
    { prop: Sort.TOTAL, display: crimeType },
    { prop: Sort.RATE, display: 'Rate/100k' },
    { prop: Sort.POPULATION, display: 'Avg. Population' },
  ];
};

const compareOrLocationRow = (
  compare,
  precision,
  id,
  allSelected,
  selectedLocationsIds,
  rest,
  onSelect,
  selectedTimespan,
  censusView,
) => {
  if (isCompare(compare)) {
    return (
      <CompareTableRow
        {...rest}
        id={id}
        precision={precision}
        key={id}
        selectedLocationsIds={selectedLocationsIds}
        onSelect={onSelect}
      />
    );
  } else {
    return (
      <LocationTableRow
        {...rest}
        id={id}
        precision={precision}
        compare={compare}
        key={id}
        selectedLocationsIds={selectedLocationsIds}
        onSelect={onSelect}
        selectedTimespan={selectedTimespan}
        censusView={censusView}
      />
    );
  }
};

class LocationsTable extends React.Component {
  componentWillReceiveProps(nextProps) {
    // Don't update table with resorted locations after a checkbox is checked.
    const unchecked =
      this.props.selectedLocationsIds.length -
        nextProps.selectedLocationsIds.length ===
      1;
    const checked =
      nextProps.selectedLocationsIds.length -
        this.props.selectedLocationsIds.length ===
      1;

    if (unchecked || checked) {
      this.sortedLocations = this.sortedLocations || this.props.locations;
    } else {
      this.sortedLocations = nextProps.locations || this.props.locations;
    }
  }

  onSelect = (event, id) => {
    const { selectLocations, locations, selectedLocationsIds } = this.props;
    const allLocations = pluck('id', locations);
    const newSelectionArray = contains(id, selectedLocationsIds)
      ? difference(selectedLocationsIds, [id])
      : append(id, selectedLocationsIds);

    selectLocations(newSelectionArray, allLocations);
  };

  onSelectAllClick = (event, status) => {
    const { selectLocations, locations } = this.props;
    const allLocations = pluck('id', locations);

    const newSelectionArray = status ? pluck('id', locations) : [];

    selectLocations(newSelectionArray, allLocations);
  };

  onCellClick = (e, prop) => {
    const { sortColumn, sortReversed, setSorting } = this.props;

    setSorting(prop, prop === sortColumn ? !sortReversed : false);
  };

  handleChangePage = (event, page) => {
    const { setSortPage } = this.props;
    const noNegatives = page < 0 ? 0 : page;
    setSortPage(noNegatives);
  };

  render() {
    const {
      compare,
      precision,
      locations,
      selectedLocationsIds,
      sortColumn,
      sortReversed,
      censusView,
      sortPage,
      timespan,
      crimeType,
    } = this.props;

    const selectedTimespan = timespan;

    if (locations.length === 0) return <EmptyTable />;

    const allSelected = locations.length === selectedLocationsIds.length;
    const order = sortReversed ? 'desc' : 'asc';
    const rows = this.sortedLocations || locations;
    const paginatedRows = rows.slice(
      sortPage * ROWS_PER_PAGE,
      sortPage * ROWS_PER_PAGE + ROWS_PER_PAGE,
    );

    return (
      <div style={{ width: '100%', overflowX: 'scroll' }}>
        <Table
          wrapperStyle={{ minWidth: isCompare(compare) ? '1020px' : '940px' }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  onChange={this.onSelectAllClick}
                  color="primary"
                />
              </TableCell>
              {getColumns(compare, censusView, crimeType).map(
                ({ prop, display }) => {
                  return (
                    <TableCell
                      key={prop}
                      style={{ cursor: 'pointer', height: 'auto' }}
                      sortDirection={sortColumn === prop ? order : false}
                    >
                      <Tooltip title="Sort" placement="bottom-end">
                        <TableSortLabel
                          active={sortColumn === prop}
                          direction={order}
                          onClick={event => this.onCellClick(event, prop)}
                        >
                          {display}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                  );
                },
              )}
            </TableRow>
          </TableHead>
          <TableBody deselectOnClickaway={false}>
            {paginatedRows.map(({ id, ...rest }) =>
              compareOrLocationRow(
                compare,
                precision,
                id,
                allSelected,
                selectedLocationsIds,
                rest,
                this.onSelect,
                selectedTimespan,
                censusView,
              ),
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={1}
                page={sortPage}
                rowsPerPage={ROWS_PER_PAGE}
                count={locations.length}
                rowsPerPageOptions={[]}
                onChangePage={this.handleChangePage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    );
  }
}

LocationsTable.defaultProps = {
  locations: [],
};

LocationsTable.propTypes = {
  precision: IPrecision.isRequired,
  censusView: bool.isRequired,

  locations: arrayOf(
    shape({
      id: string.isRequired,
      name: string.isRequired,
      population: number,
      rate: number,
      total: number,
      timespan: string,
      populationTime1: number,
      populationTime2: number,
      crimeTime1: number,
      crimeTime2: number,
      rateTime1: number,
      rateTime2: number,
    }),
  ).isRequired,

  selectedLocationsIds: arrayOf(string),
  sortColumn: ISort.isRequired,
  sortReversed: bool.isRequired,
  sortPage: number.isRequired,

  selectLocations: func.isRequired,
  setSorting: func.isRequired,
  setSortPage: func.isRequired,
};

export default LocationsTable;



// WEBPACK FOOTER //
// ./src/components/LocationTable/LocationTable.jsx